<script lang="ts">
  /**
   * App.svelte — Root component replacing M5 mount().
   * Svelte 4 syntax: export let for props, $: for reactivity, on:click for events.
   */
  import { onMount } from 'svelte';
  import { M1, M2, M3, M4, M7, M8, Logger } from './modules';
  import DotMatrixPanel from './components/DotMatrixPanel.svelte';
  import VolumeRow from './components/VolumeRow.svelte';
  import NavRow from './components/NavRow.svelte';
  import ScreenManager from './components/ScreenManager.svelte';
  import ScreenBrowser from './components/ScreenBrowser.svelte';
  import ScreenFeed from './components/ScreenFeed.svelte';
  import ScreenRecent from './components/ScreenRecent.svelte';
  import ScreenSettings from './components/ScreenSettings.svelte';
  import SlotPicker from './components/SlotPicker.svelte';
  import ConfirmDialog from './components/ConfirmDialog.svelte';
  import versionData from '../version.json';
  import type { Station, Screen } from './types';

  // Stores & composables
  import { currentScreen, showScreen } from './stores/screenStore';
  import { volume, muted, pendingPlayback, updateVolumeUI, updateMuteUI, updatePendingPlayback } from './stores/playbackStore';
  import { createTypewriter } from './composables/useTypewriter';
  import { createMarquee } from './composables/useMarquee';
  import { createConfirm } from './composables/useConfirm';

  const version: string = versionData.version;

  export let catalogError: string | null = null;

  // ── State ──────────────────────────────────────────────────
  let stationCount: number = 10;
  let currentStationIndex: number = 1;

  // Composables — destructure stores so $syntax works
  const typewriter = createTypewriter();
  const marquee = createMarquee();
  const confirm = createConfirm();

  // Extract individual stores for $-syntax usage in template
  const stationNameStore = typewriter.name;
  const metadataTextStore = marquee.metadataText;
  const scrollingStore = marquee.scrolling;
  const confirmOpenStore = confirm.isOpen;
  const confirmMessageStore = confirm.message;

  // Slot picker state
  let slotPickerOpen: boolean = false;
  let slotPickerStation: Station | null = null;

  // ── Screen Navigation ──────────────────────────────────────
  function navigateToScreen(name: Screen) { showScreen(name); }

  // ── Station Indicator ──────────────────────────────────────
  function updateStationIndicator(index: number) { currentStationIndex = index; }

  // ── Slot Picker ────────────────────────────────────────────
  function openSlotPicker(station: Station) {
    slotPickerStation = station;
    slotPickerOpen = true;
  }
  function closeSlotPicker() { slotPickerOpen = false; slotPickerStation = null; }

  // ── Browser actions ─────────────────────────────────────────
  function applySurpriseMe() {
    if (!M7 || !M2) return;
    const roster = M7.getSurpriseRoster();
    confirm.show('This will replace your current stations. Continue?', () => {
      roster.forEach((s) => {
        if (!M2!.getLocked(s.slot)) M2!.setStation(s.slot, s.url, s.label, s.url_lq, s.url_hq);
      });
    });
  }



  // ── Lifecycle: mount (replaces M5.mount()) ─────────────────
  onMount(() => {
    M1?.onMuteRampTick?.((vol: number) => updateVolumeUI(vol));
    M1?.onMuteChange?.((state) => {
      updateMuteUI(state.muted);
      M8?.onMuteChanged?.(state);
    });
    M1?.onPlaying?.(() => updatePendingPlayback(false));

    const pending = M3?.getPendingPlayback?.() ?? false;
    updatePendingPlayback(pending);

    M3?.onNavigate?.((index: number) => {
      marquee.clear();
      updateStationIndicator(index);
      const stations = M2?.getStations?.() ?? [];
      const st = stations.find((s) => s.index === index);
      typewriter.typewrite(st ? st.label : null);
      M8?.onStationChange?.(index);
      // Re-arm action handlers after station switch — on iOS,
      // changing the audio element can reset the Media Session.
      M8?.rearm?.();
      // Notify ScreenManager of station change without using the
      // single-slot M3.onNavigate callback (which this handler owns).
      window.dispatchEvent(new CustomEvent('stationchange', { detail: index }));
    });

    M4?.onLogUpdate?.(() => {
      const log = M4.getLog?.() ?? [];
      if (log.length > 0) marquee.set(log[0]);
    });

    if (M1) updateMuteUI(M1._muted || false);

    const currentIdx = M3?.getCurrentIndex?.();
    if (currentIdx) {
      updateStationIndicator(currentIdx);
      const stations = M2?.getStations?.() ?? [];
      const st = stations.find((s) => s.index === currentIdx);
      if (st) typewriter.typewrite(st.label);
      M8?.onStationChange?.(currentIdx);
    }

    Logger?.log?.({ ts: Date.now(), module: 'M5', fn: 'mount', inputs: {}, output: 'Svelte M5 mounted', durationMs: 0, error: null });
  });

  // Keep the #app element's screen-main class in sync
  $: {
    const el = document.getElementById('app');
    el?.classList.toggle('screen-main', $currentScreen === 'main');
  }

  // ── ESC key (global) ───────────────────────────────────────
  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      if (slotPickerOpen) closeSlotPicker();
      else if ($confirmOpenStore) confirm.close(false);
      else if ($currentScreen !== 'main') navigateToScreen('main');
    }
  }

  // ── OS media keys (global) ─────────────────────────────────
  function handleMediaKeys(e: KeyboardEvent) {
    switch (e.key) {
      case 'AudioVolumeUp': {
        const v = Math.min(1, $volume + 0.05);
        volume.set(v); M1?.setVolume?.(v); break;
      }
      case 'AudioVolumeDown': {
        const v = Math.max(0, $volume - 0.05);
        volume.set(v); M1?.setVolume?.(v); break;
      }
      case 'AudioVolumeMute':
        M1?.toggleMute?.(); break;
      case 'MediaTrackNext':
        M3?.next?.(); break;
      case 'MediaTrackPrevious':
        M3?.previous?.(); break;
    }
  }

  function onSupportClick() {
    Logger?.log?.({ ts: Date.now(), module: 'M5', fn: 'supportUs', inputs: {}, output: 'no SUPPORT_URL configured', durationMs: 0, level: 'debug', error: null });
  }

  function onWindowKeydown(e: KeyboardEvent) {
    handleKeydown(e);
    handleMediaKeys(e);
  }

  // ── Nav handlers ───────────────────────────────────────────
  function onPrev() { M3?.previous?.(); }
  function onNext() { M3?.next?.(); }
  function onToggleMute() { if (!$pendingPlayback) M1?.toggleMute?.(); }
</script>

<svelte:window on:keydown={onWindowKeydown} />

<div class="window">
  <!-- Main screen elements -->
  <div id="main-top">
    <img src="./assets/icon-app.png" alt="rad1o" class="app-icon">
    <div class="corner-buttons">
      <button id="btn-recent" class="corner-btn" aria-label="Recently Played"
        on:click={() => navigateToScreen('recent')}>
        <svg class="svg-icon"><use href="#icon-clock"/></svg>
      </button>
      <button id="btn-settings" class="corner-btn" aria-label="Settings"
        on:click={() => navigateToScreen('settings')}>
        <svg class="svg-icon"><use href="#icon-gear"/></svg>
      </button>
    </div>
  </div>

  <DotMatrixPanel
    stationName={$stationNameStore}
    metadataText={$metadataTextStore}
    {stationCount}
    currentIndex={currentStationIndex}
    scrolling={$scrollingStore}
    scrollDuration="10s"
  />

  <VolumeRow volume={$volume} />

  <div id="nav-row">
    <NavRow muted={$muted} pendingPlayback={$pendingPlayback} {onPrev} {onNext} {onToggleMute} />
  </div>

  <div id="support-row">
    <button id="btn-support" aria-label="Support Us" on:click={onSupportClick}>
      [ Support Us ]
    </button>
  </div>
  <p id="app-version">v{version}</p>

  <!-- Shifted screens -->
  <div id="screen-manager" class="screen" class:active={$currentScreen === 'manager'} role="tabpanel" aria-labelledby="footer-nav">
    <div class="screen-body no-scroll">
      <ScreenManager currentScreen={$currentScreen} />
    </div>
  </div>

  <div id="screen-browser" class="screen" class:active={$currentScreen === 'browser'} role="tabpanel" aria-labelledby="footer-nav">
    <div class="screen-body">
      <div class="sb-actions">
        <button id="btn-surprise" class="sb-action-btn" on:click={applySurpriseMe}>{#each '[Surprise Me!]' as char, i}<span class="rainbow-char" style="color: hsl({(i * 360 / 15)}, 70%, 40%)">{char}</span>{/each}</button>
      </div>
      <ScreenBrowser currentScreen={$currentScreen} onSlotClick={openSlotPicker} {catalogError} />
    </div>
  </div>

  <div id="screen-feed" class="screen" class:active={$currentScreen === 'feed'} role="tabpanel" aria-labelledby="footer-nav">
    <div id="feed-content" class="screen-body">
      <ScreenFeed />
    </div>
  </div>

  <div id="screen-recent" class="screen" class:active={$currentScreen === 'recent'} role="tabpanel" aria-labelledby="footer-nav">
    <div class="screen-header">
      <span class="screen-title">RECENTLY PLAYED</span>
      <button class="screen-exit" on:click={() => navigateToScreen('main')}>[X]</button>
    </div>
    <div class="screen-body">
      <ScreenRecent />
    </div>
  </div>

  <div id="screen-settings" class="screen" class:active={$currentScreen === 'settings'} role="tabpanel" aria-labelledby="footer-nav">
    <div class="screen-header">
      <span class="screen-title">SETTINGS</span>
      <button class="screen-exit" on:click={() => navigateToScreen('main')}>[X]</button>
    </div>
    <div class="screen-body">
      <ScreenSettings />
    </div>
  </div>
</div><!-- /window -->

<!-- Footer Navigation -->
<nav id="footer-nav" role="tablist" aria-label="Main navigation">
  {#each [
    { screen: 'main', label: 'play' },
    { screen: 'manager', label: 'my stations' },
    { screen: 'browser', label: 'discover' },
    { screen: 'feed', label: 'news' }
  ] as tab}
    <button
      class="footer-btn"
      class:active={$currentScreen === tab.screen}
      role="tab"
      aria-selected={$currentScreen === tab.screen}
      aria-controls="screen-{tab.screen}"
      tabindex={$currentScreen === tab.screen ? 0 : -1}
      on:click={() => navigateToScreen(tab.screen)}>
      {tab.label}
    </button>
  {/each}
</nav>

<!-- Overlays -->
<SlotPicker
  open={slotPickerOpen}
  station={slotPickerStation}
  onClose={closeSlotPicker}
  onConfirm={confirm.show}
/>

<ConfirmDialog
  open={$confirmOpenStore}
  message={$confirmMessageStore}
  onConfirm={() => confirm.close(true)}
  onCancel={() => confirm.close(false)}
/>
